import { c as _c } from "react/compiler-runtime";
// Общие детали вкладки «UGC-Артикулы» раздела «Промокоды и партнёры»:
// подписи статусов, владельцев, площадок и начислений, окно создания и правки
// платформенного артикула, окно смены статуса с причиной. Раздел только на
// русском (решение заказчика 26.09.2026), строки - прямо в разметке.
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { api, UGC_TEXTS, type UgcArticle, type UgcArticleStatus, type UgcChannel, type UgcCommissionState, type UgcOwnerKind, type UgcPartnerRole } from "@/api";
import { ProductPicker } from "@/components/product-picker";
import { CopyButton, Pill } from "@/components/promo-kit";
import { Button } from "@osmix/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@osmix/ui/dialog";
import { Input } from "@osmix/ui/input";

// ── Подписи ─────────────────────────────────────────────────────────────────

export const UGC_STATUS_LABEL: Record<UgcArticleStatus, string> = {
  moderation: "На модерации",
  active: "Активен",
  disabled: "Отключён",
  blocked: "Заблокирован",
  deleted: "Удалён"
};
const UGC_STATUS_CLASS: Record<UgcArticleStatus, string> = {
  moderation: "bg-sky-100 text-sky-700",
  active: "bg-emerald-100 text-emerald-700",
  disabled: "bg-muted text-muted-foreground",
  blocked: "bg-red-100 text-red-700",
  deleted: "bg-muted text-muted-foreground line-through"
};
export const UGC_OWNER_LABEL: Record<UgcOwnerKind, string> = {
  seller: "Продавец",
  partner: "Партнёр",
  platform: "OSMIX"
};
const UGC_OWNER_CLASS: Record<UgcOwnerKind, string> = {
  seller: "bg-amber-100 text-amber-800",
  partner: "bg-violet-100 text-violet-700",
  platform: "bg-primary/10 text-primary"
};
export const UGC_ROLE_LABEL: Record<UgcPartnerRole, string> = {
  creator: "UGC-креатор",
  arbitrage: "Арбитражник"
};
export const UGC_CHANNEL_LABEL: Record<UgcChannel, string> = {
  vk: "ВКонтакте",
  telegram: "Telegram",
  instagram: "Instagram",
  youtube: "YouTube",
  tiktok: "TikTok",
  dzen: "Дзен",
  yandex_direct: "Яндекс Директ",
  vk_ads: "VK Реклама",
  offline: "Офлайн",
  other: "Другое"
};
export const UGC_CHANNELS = Object.keys(UGC_CHANNEL_LABEL) as UgcChannel[];
export const UGC_COMMISSION_LABEL: Record<UgcCommissionState, string> = {
  hold: "Удержание (HOLD)",
  available: "Доступно к выплате",
  paid: "Выплачено",
  cancelled: "Аннулировано"
};
const UGC_COMMISSION_CLASS: Record<UgcCommissionState, string> = {
  hold: "bg-amber-100 text-amber-800",
  available: "bg-emerald-100 text-emerald-700",
  paid: "bg-sky-100 text-sky-700",
  cancelled: "bg-muted text-muted-foreground"
};
export function UgcStatusBadge(t0) {
  const $ = _c(3);
  const {
    status
  } = t0;
  const t1 = UGC_STATUS_CLASS[status];
  const t2 = UGC_STATUS_LABEL[status];
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = <Pill className={t1}>{t2}</Pill>;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  return t3;
}
export function UgcOwnerBadge(t0) {
  const $ = _c(3);
  const {
    owner
  } = t0;
  const t1 = UGC_OWNER_CLASS[owner];
  const t2 = UGC_OWNER_LABEL[owner];
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = <Pill className={t1}>{t2}</Pill>;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  return t3;
}
export function UgcCommissionBadge(t0) {
  const $ = _c(3);
  const {
    state
  } = t0;
  const t1 = UGC_COMMISSION_CLASS[state];
  const t2 = UGC_COMMISSION_LABEL[state];
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = <Pill className={t1}>{t2}</Pill>;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  return t3;
}

/** Кто ведёт артикул: продавец, партнёр (с ролью) или площадка. */
export const ugcOwnerText = (a: Pick<UgcArticle, "ownerKind" | "partnerName" | "partnerRole" | "shopName">): string => {
  if (a.ownerKind === "partner") return a.partnerName ?? "Партнёр";
  if (a.ownerKind === "seller") return a.shopName;
  return "OSMIX";
};
const selectClass = "h-9 w-full rounded-md border border-input bg-background px-3 text-sm";

// ── Создание и правка артикула ─────────────────────────────────────────────

type Draft = {
  productId: string;
  name: string;
  placement: string;
  campaign: string;
  channel: UgcChannel;
};

/**
 * Платформенный артикул под акцию OSMIX: товар, название, площадка, кампания,
 * канал. После создания окно показывает код с «Скопировать». Тем же окном
 * правятся название, площадка, кампания и канал любого артикула - товар у
 * существующего артикула не меняется (1 артикул = 1 товар).
 */
export function UgcArticleDialog({
  open,
  editing,
  onClose,
  onSaved,
  onOpenCard
}: {
  open: boolean;
  /** Правка существующего артикула; без него - создание. */
  editing?: UgcArticle;
  onClose: () => void;
  onSaved: (article: UgcArticle) => void;
  /** Создание: перейти в карточку созданного артикула. */
  onOpenCard?: (article: UgcArticle) => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => editing ? {
    productId: editing.productId,
    name: editing.name,
    placement: editing.placement,
    campaign: editing.campaign,
    channel: editing.channel
  } : {
    productId: "",
    name: "",
    placement: "",
    campaign: "",
    channel: "other"
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<UgcArticle | null>(null);
  const set = <K extends keyof Draft,>(key: K, value: Draft[K]) => setDraft(d => ({
    ...d,
    [key]: value
  }));
  const submit = async () => {
    if (!draft.productId) {
      setError("Выберите товар");
      return;
    }
    if (!draft.name.trim()) {
      setError("Укажите название артикула");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body = {
        name: draft.name.trim(),
        placement: draft.placement.trim(),
        campaign: draft.campaign.trim(),
        channel: draft.channel
      };
      if (editing) {
        onSaved(await api.ugc.update(editing.id, body));
      } else {
        const article = await api.ugc.create({
          productId: draft.productId,
          ...body
        });
        setCreated(article);
        onSaved(article);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return <Dialog open={open} onOpenChange={next => !next && onClose()}>
			<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="ugc-article-dialog">
				<DialogHeader>
					<DialogTitle>
						{created ? "Артикул создан" : editing ? `Артикул ${editing.code}` : "Платформенный артикул"}
					</DialogTitle>
					<DialogDescription>
						{created ? "Код уже работает: покупатель вводит его в поиск витрины или приложения и попадает на карточку товара." : editing ? "Название, площадка и кампания хранятся в базе, номер артикула не меняется." : "Код под акцию OSMIX: 8 цифр, привязан к одному товару. Комиссия партнёру по нему не начисляется."}
					</DialogDescription>
				</DialogHeader>

				{created ? <div className="flex flex-col items-center gap-3 rounded-lg border bg-muted/40 px-4 py-6 text-center" data-testid="ugc-created">
						<span className="font-mono text-3xl font-semibold tracking-[0.2em] tabular-nums" data-testid="ugc-created-code">
							{created.code}
						</span>
						<span className="text-sm text-muted-foreground">
							{created.productTitle} · {created.name}
						</span>
						<CopyButton value={created.code} label="Скопировать" testId="ugc-created-copy" />
					</div> : <div className="flex flex-col gap-4 text-sm">
						<div className="flex flex-col gap-1">
							<span className="text-muted-foreground">Товар</span>
							{editing ? <span className="rounded-md border px-3 py-2">{editing.productTitle}</span> : <div data-testid="ugc-product-picker">
									<ProductPicker value={draft.productId} onChange={id => set("productId", id)} placeholder="Найти товар по названию или артикулу" />
								</div>}
						</div>
						<label className="flex flex-col gap-1">
							<span className="text-muted-foreground">Название</span>
							<Input value={draft.name} maxLength={120} placeholder="ВКонтакте - Осенняя кампания" onChange={e_0 => set("name", e_0.target.value)} data-testid="ugc-name" />
						</label>
						<label className="flex flex-col gap-1">
							<span className="text-muted-foreground">Назначение / площадка</span>
							<Input value={draft.placement} maxLength={160} placeholder="VK / Сообщество бренда" onChange={e_1 => set("placement", e_1.target.value)} data-testid="ugc-placement" />
						</label>
						<div className="grid gap-4 sm:grid-cols-2">
							<label className="flex flex-col gap-1">
								<span className="text-muted-foreground">Кампания</span>
								<Input value={draft.campaign} maxLength={160} placeholder="Чёрная пятница 2026" onChange={e_2 => set("campaign", e_2.target.value)} data-testid="ugc-campaign" />
							</label>
							<label className="flex flex-col gap-1">
								<span className="text-muted-foreground">Канал</span>
								<select className={selectClass} value={draft.channel} onChange={e_3 => set("channel", e_3.target.value as UgcChannel)} data-testid="ugc-channel">
									{UGC_CHANNELS.map(c => <option key={c} value={c}>
											{UGC_CHANNEL_LABEL[c]}
										</option>)}
								</select>
							</label>
						</div>
					</div>}

				{error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}

				<DialogFooter className="gap-2">
					{created ? <>
							{onOpenCard && <Button variant="outline" onClick={() => onOpenCard(created)} data-testid="ugc-created-open">
									Открыть карточку
								</Button>}
							<Button onClick={onClose}>Готово</Button>
						</> : <>
							<Button variant="outline" onClick={onClose} disabled={busy}>
								Отмена
							</Button>
							<Button onClick={() => void submit()} disabled={busy} data-testid="ugc-save">
								{busy && <Loader2 className="mr-2 size-4 animate-spin" />}
								{editing ? "Сохранить" : "Создать артикул"}
							</Button>
						</>}
				</DialogFooter>
			</DialogContent>
		</Dialog>;
}

// ── Смена статуса ───────────────────────────────────────────────────────────

export type UgcStatusAction = "active" | "disabled" | "blocked";
export const UGC_ACTION_LABEL = (action: UgcStatusAction, current: UgcArticleStatus): string => action === "active" ? current === "moderation" ? "Одобрить" : "Активировать" : action === "disabled" ? "Отключить" : "Заблокировать";

/** Какие действия доступны из текущего статуса. Удалённый артикул не трогаем. */
export const ugcActionsFor = (status: UgcArticleStatus): UgcStatusAction[] => {
  if (status === "deleted") return [];
  const out: UgcStatusAction[] = [];
  if (status !== "active") out.push("active");
  if (status === "active" || status === "moderation") out.push("disabled");
  if (status !== "blocked") out.push("blocked");
  return out;
};
const ACTION_HINT: Record<UgcStatusAction, string> = {
  active: "Код снова распознаётся в поиске, переходы и заказы засчитываются артикулу.",
  disabled: `Покупатель по коду увидит: «${UGC_TEXTS.notFound}». Уже начисленная комиссия сохраняется.`,
  blocked: "Блокировка за накрутку или фрод: код перестаёт работать, начисления в HOLD и «Доступно к выплате» аннулируются."
};
export function UgcStatusDialog({
  article,
  action,
  onClose,
  onDone
}: {
  article: UgcArticle;
  action: UgcStatusAction;
  onClose: () => void;
  onDone: (article: UgcArticle) => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const required = action === "blocked";
  const label = UGC_ACTION_LABEL(action, article.status);
  const submit = async () => {
    if (required && !reason.trim()) {
      setError("Укажите причину блокировки - её увидят владелец артикула и журнал");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onDone(await api.ugc.update(article.id, {
        status: action,
        reason: reason.trim() || undefined
      }));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return <Dialog open onOpenChange={next => !next && onClose()}>
			<DialogContent className="sm:max-w-md" data-testid="ugc-status-dialog">
				<DialogHeader>
					<DialogTitle>
						{label} артикул {article.code}?
					</DialogTitle>
					<DialogDescription>{ACTION_HINT[action]}</DialogDescription>
				</DialogHeader>
				<label className="flex flex-col gap-1 text-sm">
					<span className="text-muted-foreground">{required ? "Причина (обязательно)" : "Причина (необязательно)"}</span>
					<textarea className="min-h-20 rounded-md border border-input bg-background px-3 py-2 text-sm" value={reason} maxLength={500} placeholder={required ? "Например: накрутка переходов с одного IP" : ""} onChange={e_0 => setReason(e_0.target.value)} data-testid="ugc-status-reason" />
				</label>
				{error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
				<DialogFooter className="gap-2">
					<Button variant="outline" onClick={onClose} disabled={busy}>
						Отмена
					</Button>
					<Button variant={action === "blocked" ? "destructive" : "default"} onClick={() => void submit()} disabled={busy || required && !reason.trim()} data-testid="ugc-status-confirm">
						{busy && <Loader2 className="mr-2 size-4 animate-spin" />}
						{label}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>;
}

/** Ссылка на товар в консоли. */
export function UgcProductLink(t0) {
  const $ = _c(3);
  const {
    id,
    title
  } = t0;
  const t1 = `/products/${encodeURIComponent(id)}`;
  let t2;
  if ($[0] !== t1 || $[1] !== title) {
    t2 = <Link className="hover:underline" to={t1}>{title}</Link>;
    $[0] = t1;
    $[1] = title;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}